import './styles.scss'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { trackProductsComparison } from '@/store/slices/adobeAnalyticSlice'
import {
  productComparisonActions,
  selectIsProductDetailsIsLoaded,
  selectIsProductDetailsIsLoading,
  selectProductDetails,
} from '@/store/slices/productComparisonSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'

import ProductComparisonTable from './components/ProductComparisonTable'
import { useGetComparisonFormLocal } from './hooks/useGetComparisonFormLocal'
import ComparisonItem from './shared/ComparisonItem'

const mergeProductDetails = (productDetails = {}, excludes = []) => {
  return Object.keys(productDetails).reduce((result, productId) => {
    const { [productId]: productData } = productDetails
    if (excludes.includes(productId) || !productData) {
      return result
    }

    return {
      ...result,
      [productId]: new ComparisonItem(productData),
    }
  }, {})
}

const ProductComparison = ({ breadcrumbs }) => {
  const { t } = useTranslation()
  const [listRemoved, setListRemoved] = useState([])
  const navigate = useNavigate()

  useAdobeAnalysis(TRACK_DATA.COMPARISON, {
    pageName: 'Products comparison',
    pageId: '19',
    pageType: PAGE_TYPES.COMPARISON,
  })

  const isLoaded = useRef(false)
  const dispatch = useAppDispatch()
  useDocumentTitle('pageTitle.productComparison')
  useBreadcrumbs(breadcrumbs)

  const isProductDetailsLoading = useAppSelector(
    selectIsProductDetailsIsLoading,
  )
  const isProductDetailsLoaded = useAppSelector(selectIsProductDetailsIsLoaded)
  const productDetails = useAppSelector(selectProductDetails)

  const { listProductIds, deleteProductIds } = useGetComparisonFormLocal()

  const memoProductDetails = JSON.stringify(productDetails)

  const listProduct = useMemo(() => {
    return mergeProductDetails(JSON.parse(memoProductDetails), listRemoved)
  }, [listRemoved, memoProductDetails])

  const isEmptyList = listProductIds.length === 0

  const shouldShowErrorMessage = !isProductDetailsLoading && !isProductDetailsLoaded

  const shouldShowTable = !shouldShowErrorMessage && !isEmptyList

  const handleRemoveProduct = (productId) => {
    setListRemoved((current) => {
      return [...current, productId]
    })

    deleteProductIds(productId)
  }

  useEffect(() => {
    if (listProductIds.length > 0 && !isLoaded.current) {
      dispatch(
        productComparisonActions.getProductComparisonData({
          materialNumber: listProductIds,
        }),
      )
      isLoaded.current = true
    }
  }, [dispatch, listProductIds])

  useEffect(() => {
    return () => dispatch(productComparisonActions.resetProductComparison())
  }, [dispatch])

  useEffect(() => {
    if (
      listProductIds.length > 0
      && listProduct
      && Object.keys(listProduct).length > 0
    ) {
      const listAnalysisProducts = (listProductIds ?? []).map(
        (productId) =>
          `${listProduct[productId]?.materialNumber}:${listProduct[productId]?.materialName}`,
      )

      dispatch(trackProductsComparison(listAnalysisProducts))
    }
  }, [dispatch, listProductIds, listProduct])

  return (
    <div className="container-full-width-page">
      <div className="product-comparison">
        <div className="product-comparison__header">
          <div className="product-comparison__header__title">
            {t('productComparison.title')}
          </div>

          <CustomButton
            buttonSize="small"
            className="back-btn"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon />
            {t('productComparison.backToPreviousPage')}
          </CustomButton>
        </div>
        <div className="product-comparison__content">
          {isEmptyList && <span>{t('productComparison.dontHaveAnyProducts')}</span>}
          {isProductDetailsLoading && <span>{t('productComparison.loading')}</span>}
          {shouldShowErrorMessage && <span>{t('productComparison.cannotGetData')}</span>}
          {shouldShowTable && (
            <ProductComparisonTable
              handleRemoveProduct={handleRemoveProduct}
              isLoaded={isProductDetailsLoaded}
              isLoading={isProductDetailsLoading}
              memoListProduct={JSON.stringify(listProduct)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductComparison
